// TablaSolicitudes.js — Merge Solponiente + SectorBXII
import { useState, useEffect } from "react";
import { Table, Button, Form, Modal, Badge, Spinner, InputGroup } from "react-bootstrap";
import FirmaElectronica from "../FirmaElectronica";

const BASE_URL = 'https://oficinavirtual-solponiente.com/api';

const TablaSolicitudes = () => {
  const [solicitudes, setSolicitudes] = useState([]);
  const [selectores, setSelectores] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [solicitudAEliminar, setSolicitudAEliminar] = useState(null);
  const [descargando, setDescargando] = useState(null);
  const [filtro, setFiltro] = useState("");
  const [estadoFiltro, setEstadoFiltro] = useState("");
  const userRol = localStorage.getItem("rol");

  const getHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  useEffect(() => {
    const fetchSolicitudes = async () => {
      try {
        const response = await fetch(
          `${BASE_URL}/solicitudes/completas`,
          { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
        );
        if (response.ok) {
          const data = await response.json();
          setSolicitudes(data);
        } else {
          console.error("Error al obtener solicitudes");
        }
      } catch (error) {
        console.error("Error al hacer la solicitud:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSolicitudes();
  }, []);

  // Descarga llamando al endpoint dedicado — sin base64 en memoria
  const handleDownload = async (documentoId, fileName) => {
    setDescargando(documentoId);
    try {
      const response = await fetch(
        `${BASE_URL}/documentos/${documentoId}/download`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      if (!response.ok) throw new Error("Error al descargar");

      const blob = await response.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = fileName || "documento.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error("Error al descargar:", error);
      alert("No se pudo descargar el documento.");
    } finally {
      setDescargando(null);
    }
  };

  const actualizarEstado = async (id, nuevoEstado) => {
    try {
      const response = await fetch(
        `${BASE_URL}/solicitudes/${id}`,
        {
          method: "PUT",
          headers: getHeaders(),
          body: JSON.stringify({ Estado: nuevoEstado }),
        }
      );
      if (response.ok) {
        const data = await response.json();
        setSolicitudes((prev) =>
          prev.map((s) =>
            s.SolicitudID === id ? { ...s, Estado: data.Estado } : s
          )
        );
      }
    } catch (error) {
      console.error("Error en la solicitud PUT:", error);
    }
  };

  const handleDelete = (id) => {
    setSolicitudAEliminar(id);
    setShowModal(true);
  };

  const confirmDelete = async () => {
    try {
      const response = await fetch(
        `${BASE_URL}/solicitudes/${solicitudAEliminar}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      if (response.ok) {
        setSolicitudes((prev) =>
          prev.filter((s) => s.SolicitudID !== solicitudAEliminar)
        );
        setShowModal(false);
        alert("Solicitud eliminada con éxito.");
      }
    } catch (error) {
      console.error("Error al eliminar:", error);
    }
  };

  const cancelarDelete = () => {
    setShowModal(false);
    setSolicitudAEliminar(null);
  };

  const activarSelector = (id) => setSelectores({ ...selectores, [id]: true });
  const desactivarSelector = (id) => setSelectores({ ...selectores, [id]: false });

  const renderEstado = (estado) => {
    const colores = {
      pendiente: "orange",
      "en proceso": "primary",
      completada: "success",
      rechazada: "danger",
    };
    const color = colores[estado] || "secondary";
    return (
      <Badge bg="white" text={color} className={`border border-${color} text-capitalize`}>
        {estado}
      </Badge>
    );
  };

  const solicitudesFiltradas = solicitudes.filter((s) => {
    const term = filtro.toLowerCase();
    const coincideTexto =
      !term ||
      s.NumeroRegistro?.toLowerCase().includes(term) ||
      s.Usuario?.Nombre?.toLowerCase().includes(term) ||
      s.Usuario?.Apellidos?.toLowerCase().includes(term) ||
      s.Usuario?.Num_Identificacion?.toLowerCase().includes(term);
    const coincideEstado = !estadoFiltro || s.Estado === estadoFiltro;
    return coincideTexto && coincideEstado;
  });

  if (loading) return <p className="mt-3">Cargando solicitudes...</p>;

  return (
    <div className="mt-4">
      <div className="d-flex flex-column flex-md-row gap-2 mb-3">
        <InputGroup>
          <InputGroup.Text><i className="bi bi-search"></i></InputGroup.Text>
          <Form.Control
            placeholder="Buscar por DNI, nombre o número de registro…"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
          />
          {filtro && <Button variant="outline-secondary" onClick={() => setFiltro("")}>✕</Button>}
        </InputGroup>
        <Form.Select
          value={estadoFiltro}
          onChange={(e) => setEstadoFiltro(e.target.value)}
          style={{ maxWidth: 200 }}
        >
          <option value="">Todos los estados</option>
          <option value="pendiente">Pendiente</option>
          <option value="en proceso">En proceso</option>
          <option value="completada">Completada</option>
          <option value="rechazada">Rechazada</option>
        </Form.Select>
      </div>

      {solicitudesFiltradas.length === 0 ? (
        <p className="text-muted">No hay solicitudes que coincidan con el filtro.</p>
      ) : (
      <div className="table-responsive">
        <Table hover className="align-middle">
          <thead className="thead-brand thead-label">
            <tr>
              <th>Nº Registro</th>
              <th>Usuario</th>
              <th>Descripción</th>
              <th>Fecha de solicitud</th>
              <th>Archivo adjunto</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {solicitudesFiltradas.map((s) => {
              const esCertificado = s.TipoSolicitud === "certificado";
              // La descarga solo tiene sentido cuando hay un DocumentoID real:
              // una solicitud de certificado recien creada no tiene ninguno
              // todavia (se genera desde la pestaña "Certificados"), así que
              // sin esta comprobación "descargando === s.DocumentoID" daba
              // null === null y el botón se quedaba con el spinner fijo.
              const descargandoEsteDocumento =
                s.DocumentoID != null && descargando === s.DocumentoID;

              return (
                <tr key={s.SolicitudID}>
                  <td>
                    <code className="small text-primary fw-bold">{s.NumeroRegistro || "—"}</code>
                    {!s.Visto && (
                      <span className="badge bg-danger rounded-pill ms-2">Nueva</span>
                    )}
                  </td>
                  <td className="text-nowrap small">
                    {s.Usuario.Nombre} {s.Usuario.Apellidos}
                  </td>
                  <td className="small">{s.Descripcion}</td>
                  <td className="small">{new Date(s.FechaSolicitud).toLocaleDateString("es-ES")}</td>
                  <td>
                    {esCertificado ? (
                      <Badge bg="white" text="secondary" className="border border-secondary fw-normal">
                        <i className="bi bi-patch-check me-1"></i>
                        Certificado
                      </Badge>
                    ) : (
                      <>
                        <div className="small text-muted">{s.NombreDocumento}</div>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="mt-1"
                          disabled={descargandoEsteDocumento}
                          onClick={() => handleDownload(s.DocumentoID, s.NombreDocumento)}
                        >
                          {descargandoEsteDocumento ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            <><i className="bi bi-download"></i> Descargar</>
                          )}
                        </Button>
                      </>
                    )}
                  </td>
                  <td>
                    {selectores[s.SolicitudID] ? (
                      <Form.Select
                        size="sm"
                        value={s.Estado}
                        onChange={(e) => actualizarEstado(s.SolicitudID, e.target.value)}
                      >
                        <option value="pendiente">Pendiente</option>
                        <option value="en proceso">En proceso</option>
                        <option value="completada">Completada</option>
                        <option value="rechazada">Rechazada</option>
                      </Form.Select>
                    ) : (
                      renderEstado(s.Estado)
                    )}
                  </td>
                  <td>
                    <div className="d-flex flex-column gap-1" style={{ minWidth: 190 }}>
                      {esCertificado ? null : (userRol === "admin" && !s.FirmadoAdmin) ||
                        (userRol === "usuario" && !s.FirmadoUser) ? (
                        // FirmaElectronica ya no recibe el base64 — ahora recibe el DocumentoID
                        // para que el componente descargue el fichero cuando lo necesite
                        <FirmaElectronica
                          documentoId={s.DocumentoID}
                          solicitudId={s.SolicitudID}
                        />
                      ) : (
                        <Button variant="outline-success" size="sm" className="w-100" disabled>
                          <i className="bi bi-check-lg"></i> Firmado
                        </Button>
                      )}

                      <div className="d-flex gap-1">
                        {!selectores[s.SolicitudID] ? (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="flex-fill"
                            onClick={() => activarSelector(s.SolicitudID)}
                          >
                            <i className="bi bi-pencil"></i> Estado
                          </Button>
                        ) : (
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="flex-fill"
                            onClick={() => {
                              actualizarEstado(s.SolicitudID, s.Estado);
                              desactivarSelector(s.SolicitudID);
                            }}
                          >
                            <i className="bi bi-check-lg"></i> Aceptar
                          </Button>
                        )}

                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="flex-fill"
                          onClick={() => handleDelete(s.SolicitudID)}
                        >
                          <i className="bi bi-trash"></i> Eliminar
                        </Button>
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
      )}

      <Modal show={showModal} onHide={cancelarDelete} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Eliminación</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          ¿Estás seguro de que deseas eliminar esta solicitud? Esta acción no se
          puede deshacer.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={cancelarDelete}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Eliminar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TablaSolicitudes;
